<script setup lang="ts">
import { Head, router, useHttp } from '@inertiajs/vue3';
import { useEcho } from '@laravel/echo-vue';
import {
    Copy,
    Eye,
    FileText,
    Link2,
    Loader2,
    Search,
    Sparkles,
    X,
} from 'lucide-vue-next';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import {
    autoMatch as autoMatchAction,
    deduplicate as deduplicateAction,
    destroy,
    store as associationStore,
} from '@/actions/App/Http/Controllers/AssociationController';
import { download as fileDownload } from '@/actions/App/Http/Controllers/FileController';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import type { LibraryFile } from '@/types';

interface AssociatedPdf {
    id: string;
    original_filename: string;
    paper_file_id: string;
}

interface Paper {
    id: string;
    title: string;
    authors: string[] | null;
    year: number | null;
    doi: string | null;
    pdfs: AssociatedPdf[];
}

interface Project {
    id: string;
    name: string;
}

interface Props {
    project: Project;
    papers: Paper[];
    files: LibraryFile[];
    duplicates_count: number;
}

const props = defineProps<Props>();
const toast = useToast();

// Lazy-load PdfViewer
const PdfViewer = defineAsyncComponent(
    () => import('@/components/shared/PdfViewer.vue'),
);

defineOptions({
    layout: (p: { project: Project }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: p.project.name,
                    href: `/projects/${p.project.id}`,
                },
                { title: 'Rattacher PDF aux articles', href: '#' },
            ],
        },
    ],
});

// ─── State ────────────────────────────────────────────────────────────────────

const selectedPaperId = ref<string | null>(null);
const selectedPdfIds = ref<Set<string>>(new Set());
const previewPdfId = ref<string | null>(null);
const previewPdfName = ref<string>('');
const isPreviewOpen = ref(false);

const searchPapers = ref('');
const searchFiles = ref('');
const isAssociating = ref(false);
const isAutoMatching = ref(false);
const isUnlinking = ref<string | null>(null);
const isDeduplicating = ref(false);

// ─── Derived from selected paper ──────────────────────────────────────────────

const selectedPaper = computed(
    () => props.papers.find((p) => p.id === selectedPaperId.value) ?? null,
);

const currentPdfs = computed(() => selectedPaper.value?.pdfs ?? []);

const currentPdfIds = computed(
    () => new Set(currentPdfs.value.map((p) => p.id)),
);

// ─── Filtered lists ───────────────────────────────────────────────────────────

const filteredPapers = computed(() => {
    if (!searchPapers.value) {
        return props.papers;
    }

    const q = searchPapers.value.toLowerCase();

    return props.papers.filter(
        (p) =>
            p.title.toLowerCase().includes(q) ||
            p.authors?.some((a) => a.toLowerCase().includes(q)) ||
            p.doi?.toLowerCase().includes(q),
    );
});

const filteredLibraryFiles = computed(() => {
    const notLinked = props.files.filter((f) => !currentPdfIds.value.has(f.id));

    if (!searchFiles.value) {
        return notLinked;
    }

    const q = searchFiles.value.toLowerCase();

    return notLinked.filter((f) =>
        f.original_filename.toLowerCase().includes(q),
    );
});

// ─── Stats ────────────────────────────────────────────────────────────────────

const totalPdfCount = computed(() => props.files.length);
const associatedPaperCount = computed(
    () => props.papers.filter((p) => p.pdfs.length > 0).length,
);
const linkedFileIds = computed(() => {
    const ids = new Set<string>();

    for (const p of props.papers) {
        for (const pdf of p.pdfs) {
            ids.add(pdf.id);
        }
    }

    return ids;
});
const linkedFileCount = computed(() => linkedFileIds.value.size);
const unlinkedFileCount = computed(
    () => totalPdfCount.value - linkedFileCount.value,
);

// ─── Realtime ─────────────────────────────────────────────────────────────────

if (!import.meta.env.SSR) {
    useEcho(`project.${props.project.id}`, '.project.file_uploaded', () => {
        toast.success('Nouveau fichier importé dans la bibliothèque.');
        router.reload({ only: ['files'] });
    });

    useEcho(`project.${props.project.id}`, '.project.pdf_matched', () => {
        toast.success('Association PDF détectée automatiquement.');
        router.reload({ only: ['papers', 'files'] });
    });
}

// ─── Actions ──────────────────────────────────────────────────────────────────

function selectPaper(paperId: string) {
    selectedPaperId.value = paperId;
    selectedPdfIds.value.clear();
    searchFiles.value = '';
}

function toggleFile(fileId: string) {
    if (selectedPdfIds.value.has(fileId)) {
        selectedPdfIds.value.delete(fileId);
    } else {
        selectedPdfIds.value.add(fileId);
    }
}

function openPreview(pdfId: string, filename: string) {
    previewPdfId.value = pdfId;
    previewPdfName.value = filename;
    isPreviewOpen.value = true;
}

const http = useHttp({ paper_id: '', pdf_ids: [] as string[] });

async function associate() {
    if (!selectedPaperId.value || selectedPdfIds.value.size === 0) {
        return;
    }

    isAssociating.value = true;

    const selectedCount = selectedPdfIds.value.size;

    http.paper_id = selectedPaperId.value;
    http.pdf_ids = Array.from(selectedPdfIds.value);

    try {
        await http.post(associationStore.url(props.project.id));

        toast.success(`${selectedCount} fichier(s) associé(s).`);
        selectedPdfIds.value.clear();
        router.reload({ only: ['papers', 'files'] });
    } catch {
        toast.error("Erreur lors de l'association.");
    } finally {
        isAssociating.value = false;
    }
}

const unlinkHttp = useHttp({});

async function unlink(paperFileId: string) {
    isUnlinking.value = paperFileId;

    try {
        await unlinkHttp.delete(
            destroy.url({
                project: props.project.id,
                paperFile: paperFileId,
            }),
        );

        toast.success('PDF dissocié.');
        router.reload({ only: ['papers', 'files'] });
    } catch {
        toast.error('Erreur lors de la dissociation.');
    } finally {
        isUnlinking.value = null;
    }
}

const autoMatchHttp = useHttp({});

async function runAutoMatch() {
    isAutoMatching.value = true;

    try {
        await autoMatchHttp.post(autoMatchAction.url(props.project.id));

        toast.success('Détection automatique lancée.');
    } catch {
        toast.error('Erreur lors de la détection automatique.');
    } finally {
        isAutoMatching.value = false;
    }
}

const deduplicateHttp = useHttp({});

async function runDeduplicate() {
    isDeduplicating.value = true;

    try {
        const res = (await deduplicateHttp.post(
            deduplicateAction.url(props.project.id),
        )) as { message?: string };

        toast.success(res.message ?? 'Doublons supprimés.');
        router.reload({ only: ['papers', 'files'] });
    } catch {
        toast.error('Erreur lors de la suppression des doublons.');
    } finally {
        isDeduplicating.value = false;
    }
}

function formatSize(bytes: number) {
    if (bytes === 0) {
        return '0 B';
    }

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Keep selected paper's pdfs in sync on Inertia reload
watch(
    () => props.papers,
    () => {
        // Re-sync selectedPdfIds after reload (remove any now-linked files)
        if (selectedPaper.value) {
            const linkedIds = new Set(
                selectedPaper.value.pdfs.map((p) => p.id),
            );

            for (const id of selectedPdfIds.value) {
                if (linkedIds.has(id)) {
                    selectedPdfIds.value.delete(id);
                }
            }
        }
    },
    { deep: true },
);
</script>

<template>
    <Head :title="`Associer PDF — ${project.name}`" />

    <div class="flex h-full flex-col overflow-y-auto">
        <!-- Page header -->
        <div class="shrink-0 border-b border-border/40 px-6 py-5">
            <div class="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div
                        class="text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        Préparation
                    </div>
                    <h1 class="mt-1 text-2xl font-semibold tracking-tight">
                        Associer les PDF aux articles
                    </h1>
                    <p class="mt-1 text-sm text-muted-foreground">
                        Liez chaque fichier PDF de votre bibliothèque à
                        l'article correspondant dans le projet.
                    </p>
                </div>
            </div>

            <!-- AI banner -->
            <div
                data-tour="auto-match-btn"
                class="mt-4 flex items-center gap-4 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 dark:border-brand/30 dark:bg-brand/10"
            >
                <Sparkles class="h-5 w-5 shrink-0 text-brand" />
                <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-foreground">
                        Détection automatique
                    </p>
                    <p class="text-xs text-muted-foreground">
                        L'assistant peut associer automatiquement les PDF à
                        partir des métadonnées (DOI, titre, auteurs).
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    class="shrink-0 border-brand/30 bg-card text-brand hover:bg-brand/5"
                    :disabled="isAutoMatching"
                    @click="runAutoMatch"
                >
                    <Loader2
                        v-if="isAutoMatching"
                        class="mr-1.5 h-4 w-4 animate-spin"
                    />
                    <Sparkles v-else class="mr-1.5 h-4 w-4" />
                    {{ isAutoMatching ? 'Détection…' : 'Lancer la détection' }}
                </Button>
            </div>

            <!-- Duplicates banner -->
            <div
                v-if="duplicates_count > 0"
                class="mt-3 flex items-center gap-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800/50 dark:bg-amber-900/20"
            >
                <Copy
                    class="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                />
                <div class="min-w-0 flex-1">
                    <p
                        class="text-sm font-medium text-amber-900 dark:text-amber-200"
                    >
                        {{ duplicates_count }} nom{{
                            duplicates_count > 1 ? 's' : ''
                        }}
                        de fichier en double détecté{{
                            duplicates_count > 1 ? 's' : ''
                        }}
                    </p>
                    <p class="text-xs text-amber-700 dark:text-amber-400">
                        Les associations existantes seront migrées vers la copie
                        conservée.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    class="shrink-0 border-amber-300 bg-white text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:bg-transparent dark:text-amber-300"
                    :disabled="isDeduplicating"
                    @click="runDeduplicate"
                >
                    <Loader2
                        v-if="isDeduplicating"
                        class="mr-1.5 h-4 w-4 animate-spin"
                    />
                    <Copy v-else class="mr-1.5 h-4 w-4" />
                    {{
                        isDeduplicating
                            ? 'Suppression…'
                            : 'Supprimer les doublons'
                    }}
                </Button>
            </div>

            <!-- Summary stats -->
            <div class="mt-4 grid grid-cols-4 gap-3">
                <div
                    class="rounded-lg border border-border bg-card p-3 text-center"
                >
                    <div class="text-2xl font-bold tabular-nums">
                        {{ totalPdfCount }}
                    </div>
                    <div class="mt-0.5 text-xs text-muted-foreground">
                        PDF importés
                    </div>
                </div>
                <div
                    class="rounded-lg border border-border bg-card p-3 text-center"
                >
                    <div
                        class="text-2xl font-bold tabular-nums"
                        :class="
                            linkedFileCount > 0
                                ? 'text-green-600'
                                : 'text-foreground'
                        "
                    >
                        {{ linkedFileCount }}
                    </div>
                    <div class="mt-0.5 text-xs text-muted-foreground">
                        PDF associés
                    </div>
                </div>
                <div
                    class="rounded-lg border border-border bg-card p-3 text-center"
                >
                    <div
                        class="text-2xl font-bold tabular-nums"
                        :class="
                            unlinkedFileCount > 0 && totalPdfCount > 0
                                ? 'text-amber-600'
                                : 'text-foreground'
                        "
                    >
                        {{ unlinkedFileCount }}
                    </div>
                    <div class="mt-0.5 text-xs text-muted-foreground">
                        Sans association
                    </div>
                </div>
                <div
                    class="rounded-lg border border-border bg-card p-3 text-center"
                >
                    <div class="text-2xl font-bold tabular-nums">
                        {{ associatedPaperCount }}
                    </div>
                    <div class="mt-0.5 text-xs text-muted-foreground">
                        Articles avec PDF
                    </div>
                </div>
            </div>
        </div>

        <!-- Split layout -->
        <div class="grid min-h-0 flex-1 grid-cols-2 divide-x divide-border">
            <!-- Left panel: Articles -->
            <div class="flex min-h-0 flex-col">
                <div class="shrink-0 border-b border-border px-4 py-3">
                    <h2 class="text-sm font-semibold">
                        Articles ({{ papers.length }})
                    </h2>
                    <div class="relative mt-2">
                        <Search
                            class="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground"
                        />
                        <Input
                            v-model="searchPapers"
                            type="search"
                            placeholder="Rechercher un article..."
                            class="h-9 pl-9 text-sm"
                        />
                    </div>
                </div>
                <div
                    data-tour="associations-paper-list"
                    class="flex-1 overflow-y-auto"
                >
                    <button
                        v-for="paper in filteredPapers"
                        :key="paper.id"
                        class="flex w-full items-start gap-3 border-b border-border/50 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                        :class="{
                            'bg-brand/5 ring-1 ring-brand/20 ring-inset':
                                selectedPaperId === paper.id,
                        }"
                        @click="selectPaper(paper.id)"
                    >
                        <FileText
                            class="mt-0.5 h-4 w-4 shrink-0"
                            :class="
                                paper.pdfs.length > 0
                                    ? 'text-green-500'
                                    : 'text-muted-foreground/40'
                            "
                        />
                        <div class="min-w-0 flex-1">
                            <p
                                class="line-clamp-2 text-sm leading-snug font-medium"
                            >
                                {{ paper.title }}
                            </p>
                            <p
                                v-if="paper.authors?.length"
                                class="mt-0.5 truncate text-xs text-muted-foreground"
                            >
                                {{ paper.authors.slice(0, 3).join(', ')
                                }}{{ paper.authors.length > 3 ? '…' : '' }}
                            </p>
                        </div>
                        <Badge
                            v-if="paper.pdfs.length > 0"
                            variant="secondary"
                            class="shrink-0 border-emerald-200 bg-emerald-100 text-emerald-700 tabular-nums dark:border-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400"
                        >
                            {{ paper.pdfs.length }} PDF
                        </Badge>
                    </button>
                    <p
                        v-if="filteredPapers.length === 0"
                        class="py-8 text-center text-sm text-muted-foreground italic"
                    >
                        Aucun article trouvé.
                    </p>
                </div>
            </div>

            <!-- Right panel -->
            <div
                data-tour="associations-right-panel"
                class="flex min-h-0 flex-col"
            >
                <!-- Empty state: no paper selected -->
                <div
                    v-if="!selectedPaperId"
                    class="flex flex-1 items-center justify-center p-8"
                >
                    <div class="text-center">
                        <FileText
                            class="mx-auto mb-3 h-8 w-8 text-muted-foreground/30"
                        />
                        <p class="text-sm font-medium text-muted-foreground">
                            Sélectionnez un article
                        </p>
                        <p class="mt-1 text-xs text-muted-foreground/70">
                            Choisissez un article à gauche pour gérer ses PDF
                            associés.
                        </p>
                    </div>
                </div>

                <template v-else>
                    <!-- Section 1: PDFs already linked to this paper -->
                    <div class="shrink-0 border-b border-border">
                        <div
                            class="flex items-center justify-between px-4 py-3"
                        >
                            <h3 class="text-sm font-semibold">
                                PDFs associés
                                <span
                                    class="ml-1 text-xs font-normal text-muted-foreground"
                                    >({{ currentPdfs.length }})</span
                                >
                            </h3>
                        </div>

                        <div v-if="currentPdfs.length === 0" class="px-4 pb-3">
                            <p class="text-xs text-muted-foreground italic">
                                Aucun PDF associé à cet article.
                            </p>
                        </div>

                        <div v-else class="max-h-48 overflow-y-auto">
                            <div
                                v-for="pdf in currentPdfs"
                                :key="pdf.paper_file_id"
                                class="flex items-center gap-2 border-b border-border/40 px-4 py-2 last:border-b-0"
                            >
                                <FileText
                                    class="h-3.5 w-3.5 shrink-0 text-brand"
                                />
                                <p
                                    class="min-w-0 flex-1 truncate text-sm"
                                    :title="pdf.original_filename"
                                >
                                    {{ pdf.original_filename }}
                                </p>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    class="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                                    aria-label="Aperçu du PDF"
                                    @click="
                                        openPreview(
                                            pdf.id,
                                            pdf.original_filename,
                                        )
                                    "
                                >
                                    <Eye class="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    class="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                                    :disabled="
                                        isUnlinking === pdf.paper_file_id
                                    "
                                    aria-label="Dissocier ce PDF"
                                    @click="unlink(pdf.paper_file_id)"
                                >
                                    <Loader2
                                        v-if="isUnlinking === pdf.paper_file_id"
                                        class="h-3.5 w-3.5 animate-spin"
                                    />
                                    <X v-else class="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    <!-- Section 2: Library PDFs to add -->
                    <div
                        data-tour="manual-link-section"
                        class="flex min-h-0 flex-1 flex-col"
                    >
                        <div class="shrink-0 px-4 py-3">
                            <h3 class="text-sm font-semibold">
                                Ajouter depuis la bibliothèque
                                <span
                                    class="ml-1 text-xs font-normal text-muted-foreground"
                                    >({{
                                        filteredLibraryFiles.length
                                    }}
                                    disponibles)</span
                                >
                            </h3>
                            <div class="relative mt-2">
                                <Search
                                    class="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground"
                                />
                                <Input
                                    v-model="searchFiles"
                                    type="search"
                                    placeholder="Rechercher un fichier..."
                                    class="h-9 pl-9 text-sm"
                                />
                            </div>
                        </div>

                        <div class="min-h-0 flex-1 overflow-y-auto">
                            <div
                                v-for="file in filteredLibraryFiles"
                                :key="file.id"
                                class="flex items-center gap-3 border-b border-border/40 px-4 py-2.5 transition-colors hover:bg-muted/50"
                                :class="{
                                    'bg-brand/5': selectedPdfIds.has(file.id),
                                }"
                            >
                                <Checkbox
                                    :model-value="selectedPdfIds.has(file.id)"
                                    class="shrink-0"
                                    @update:model-value="toggleFile(file.id)"
                                />
                                <div class="min-w-0 flex-1">
                                    <p
                                        class="truncate text-sm font-medium"
                                        :title="file.original_filename"
                                    >
                                        {{ file.original_filename }}
                                    </p>
                                    <p class="text-xs text-muted-foreground">
                                        {{ formatSize(file.size_bytes) }}
                                        <template
                                            v-if="file.projects.length > 0"
                                        >
                                            ·
                                            {{
                                                file.projects
                                                    .map((p) => p.name)
                                                    .join(', ')
                                            }}
                                        </template>
                                    </p>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    class="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                                    aria-label="Aperçu du PDF"
                                    @click="
                                        openPreview(
                                            file.id,
                                            file.original_filename,
                                        )
                                    "
                                >
                                    <Eye class="h-3.5 w-3.5" />
                                </Button>
                            </div>
                            <p
                                v-if="filteredLibraryFiles.length === 0"
                                class="py-8 text-center text-sm text-muted-foreground italic"
                            >
                                {{
                                    props.files.length === currentPdfs.length
                                        ? 'Tous les PDF de la bibliothèque sont déjà associés à cet article.'
                                        : 'Aucun fichier trouvé.'
                                }}
                            </p>
                        </div>

                        <!-- Associate button -->
                        <div
                            v-if="selectedPdfIds.size > 0"
                            class="shrink-0 border-t border-border px-4 py-3"
                        >
                            <Button
                                class="w-full gap-2 bg-brand hover:bg-brand/90"
                                :disabled="isAssociating"
                                @click="associate"
                            >
                                <Loader2
                                    v-if="isAssociating"
                                    class="h-4 w-4 animate-spin"
                                />
                                <Link2 v-else class="h-4 w-4" />
                                Associer {{ selectedPdfIds.size }} fichier{{
                                    selectedPdfIds.size > 1 ? 's' : ''
                                }}
                            </Button>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <!-- PDF Viewer -->
        <PdfViewer
            v-if="previewPdfId"
            :open="isPreviewOpen"
            :pdfs="
                previewPdfId
                    ? [{ id: previewPdfId, original_filename: previewPdfName }]
                    : []
            "
            :resolve-url="
                (fileId: string) =>
                    fileDownload.url({ project: project.id, file: fileId })
            "
            @update:open="isPreviewOpen = $event"
        />
    </div>
</template>
